import { useEffect, useRef } from "react";

type Star = {
  x: number;
  y: number;
  edgeField: boolean;
  radius: number;
  alpha: number;
  twinkle: number;
  phase: number;
  depth: number;
  speed: number;
  glow: number;
  color: {
    r: number;
    g: number;
    b: number;
  };
};

type Meteor = {
  active: boolean;
  delay: number;
  elapsed: number;
  duration: number;
  progress: number;
  x: number;
  y: number;
  length: number;
  dx: number;
  dy: number;
  distance: number;
};

const STAR_COUNT = 7200;

function createRandom(seed: number) {
  let value = seed;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 4294967296;
  };
}

function gaussian(random: () => number) {
  const u = Math.max(random(), 0.0001);
  const v = Math.max(random(), 0.0001);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function smoothStep(edge0: number, edge1: number, value: number) {
  const progress = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return progress * progress * (3 - 2 * progress);
}

function pickStarColor(coreStrength: number, random: () => number) {
  const roll = random();

  if (coreStrength > 0.64 && roll > 0.56) {
    return roll > 0.86 ? { r: 255, g: 217, b: 176 } : { r: 188, g: 216, b: 255 };
  }

  if (roll > 0.86) {
    return { r: 124, g: 185, b: 255 };
  }

  if (roll > 0.76) {
    return { r: 224, g: 231, b: 255 };
  }

  if (roll < 0.05) {
    return { r: 255, g: 197, b: 218 };
  }

  return { r: 199, g: 221, b: 255 };
}

function createStars(width: number, height: number) {
  const random = createRandom(20260612);
  const stars: Star[] = [];
  const diagonal = Math.hypot(width, height);
  const clusterAnchors = [0.16, 0.34, 0.52, 0.68, 0.84];

  for (let index = 0; index < STAR_COUNT; index += 1) {
    const inEdgeField = random() < 0.8;
    const inCluster = !inEdgeField && random() < 0.24;
    const inBand = !inEdgeField && (inCluster || random() < 0.58);
    const clusterAnchor = clusterAnchors[Math.floor(random() * clusterAnchors.length)];
    const alongBand = inCluster
      ? Math.max(0, Math.min(1, clusterAnchor + gaussian(random) * 0.035))
      : random();
    const bandX = alongBand * width;
    const bandCurve = Math.sin(alongBand * Math.PI * 2.2 + 0.7) * height * 0.055;
    const bandCenterY = height * 0.52 + (alongBand - 0.5) * height * 0.08 + bandCurve;
    const bandWidth = diagonal * (inCluster ? 0.032 + random() * 0.04 : random() < 0.72 ? 0.075 : 0.16);
    const distance = gaussian(random) * bandWidth;
    let x = inBand ? bandX + gaussian(random) * width * (inCluster ? 0.014 : 0.026) : random() * width;
    let y = inBand ? bandCenterY + distance * 3 : random() * height;

    if (inEdgeField) {
      const edgeSide = Math.floor(random() * 4);
      const edgeDepth = Math.pow(random(), 1.8);
      const edgeInsetX = width * (0.01 + edgeDepth * 0.32);
      const edgeInsetY = height * (0.01 + edgeDepth * 0.32);

      if (edgeSide === 0) {
        x = edgeInsetX;
        y = random() * height;
      } else if (edgeSide === 1) {
        x = width - edgeInsetX;
        y = random() * height;
      } else if (edgeSide === 2) {
        x = random() * width;
        y = edgeInsetY;
      } else {
        x = random() * width;
        y = height - edgeInsetY;
      }
    }

    const distanceFromBand = Math.min(1, Math.abs(distance) / Math.max(1, bandWidth * 2.8));
    const coreStrength = inBand ? Math.max(0, 1 - distanceFromBand) : 0;
    const clusterStrength = inBand && inCluster ? Math.max(0, 1 - Math.abs(alongBand - clusterAnchor) * 18) : 0;
    const centralGlow = inBand ? Math.max(0, 1 - Math.abs(alongBand - 0.66) * 3.2) : 0;
    const sizeRoll = random();
    const radius = sizeRoll > 0.994
      ? 1.45 + random() * 0.8
      : sizeRoll > 0.955
        ? 0.82 + random() * 0.58
        : 0.18 + random() * (0.5 + coreStrength * 0.22 + clusterStrength * 0.08);
    const scaledRadius = radius * 1.1;

    stars.push({
      x: (x + width) % width,
      y: (y + height) % height,
      edgeField: inEdgeField,
      radius: scaledRadius,
      alpha: 0.07 + random() * 0.32 + coreStrength * 0.14 + centralGlow * 0.05 + clusterStrength * 0.08 + (inEdgeField ? 0.035 : 0),
      twinkle: 0.03 + random() * 0.12 + coreStrength * 0.06 + clusterStrength * 0.04,
      phase: random() * Math.PI * 2,
      depth: random(),
      speed: 0.000011 + random() * 0.000021 + coreStrength * 0.000006,
      glow: radius > 0.85 ? 0.75 + random() * 1.3 : random() * 0.32,
      color: pickStarColor(Math.max(coreStrength, centralGlow), random)
    });
  }

  return stars;
}

function createMeteor(width: number, height: number, random: () => number): Meteor {
  const side = Math.floor(random() * 4);
  let x = 0;
  let y = 0;

  if (side === 0) {
    x = -width * (0.08 + random() * 0.26);
    y = random() * height;
  } else if (side === 1) {
    x = width * (1.08 + random() * 0.26);
    y = random() * height;
  } else if (side === 2) {
    x = random() * width;
    y = -height * (0.08 + random() * 0.22);
  } else {
    x = random() * width;
    y = height * (1.08 + random() * 0.22);
  }

  const targetX = width * (0.18 + random() * 0.64);
  const targetY = height * (0.16 + random() * 0.68);
  const angle = Math.atan2(targetY - y, targetX - x) + (random() - 0.5) * 0.55;
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);

  return {
    active: true,
    delay: 900 + random() * 2600,
    elapsed: 0,
    duration: 420 + random() * 460,
    progress: 0,
    x,
    y,
    length: 180 + random() * 260,
    dx,
    dy,
    distance: Math.hypot(width, height) * (0.72 + random() * 0.36)
  };
}

export function GalaxyBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d", { alpha: true });
    if (!context) return;
    const canvasElement = canvas;
    const ctx = context;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
    let width = window.innerWidth;
    let height = window.innerHeight;
    let stars = createStars(width, height);
    const galaxyCloudCanvas = document.createElement("canvas");
    const galaxyCloudContext = galaxyCloudCanvas.getContext("2d", { alpha: true });
    let animationFrame = 0;
    let lastTime = performance.now();
    const frameInterval = 1000 / 30;
    let meteor: Meteor = {
      active: false,
      delay: 900,
      elapsed: 0,
      duration: 600,
      progress: 0,
      x: 0,
      y: 0,
      length: 260,
      dx: 1,
      dy: 0.2,
      distance: 1000
    };
    const random = createRandom(1218);

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvasElement.width = Math.floor(width * pixelRatio);
      canvasElement.height = Math.floor(height * pixelRatio);
      canvasElement.style.width = `${width}px`;
      canvasElement.style.height = `${height}px`;
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      stars = createStars(width, height);
      renderGalaxyCloud();
    }

    function renderGalaxyCloud() {
      if (!galaxyCloudContext) return;

      galaxyCloudCanvas.width = Math.floor(width * pixelRatio);
      galaxyCloudCanvas.height = Math.floor(height * pixelRatio);
      galaxyCloudContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      galaxyCloudContext.clearRect(0, 0, width, height);
      galaxyCloudContext.save();
      galaxyCloudContext.translate(width * 0.5, height * 0.52);
      galaxyCloudContext.rotate(-0.045);
      galaxyCloudContext.filter = "blur(24px)";

      const clouds = [
        { x: -width * 0.34, y: height * 0.02, rx: width * 0.26, ry: height * 0.11, color: "118, 155, 226", alpha: 0.075 },
        { x: -width * 0.06, y: -height * 0.01, rx: width * 0.32, ry: height * 0.13, color: "74, 112, 190", alpha: 0.095 },
        { x: width * 0.22, y: height * 0.025, rx: width * 0.23, ry: height * 0.1, color: "196, 158, 139", alpha: 0.06 },
        { x: width * 0.42, y: -height * 0.015, rx: width * 0.22, ry: height * 0.09, color: "58, 92, 166", alpha: 0.055 }
      ];

      clouds.forEach((cloud) => {
        const gradient = galaxyCloudContext.createRadialGradient(cloud.x, cloud.y, 0, cloud.x, cloud.y, Math.max(cloud.rx, cloud.ry));
        gradient.addColorStop(0, `rgba(${cloud.color}, ${cloud.alpha})`);
        gradient.addColorStop(0.42, `rgba(${cloud.color}, ${cloud.alpha * 0.42})`);
        gradient.addColorStop(0.76, `rgba(${cloud.color}, ${cloud.alpha * 0.12})`);
        gradient.addColorStop(1, `rgba(${cloud.color}, 0)`);
        galaxyCloudContext.globalAlpha = 1;
        galaxyCloudContext.fillStyle = gradient;
        galaxyCloudContext.save();
        galaxyCloudContext.translate(cloud.x, cloud.y);
        galaxyCloudContext.scale(1, cloud.ry / cloud.rx);
        galaxyCloudContext.beginPath();
        galaxyCloudContext.arc(0, 0, cloud.rx, 0, Math.PI * 2);
        galaxyCloudContext.fill();
        galaxyCloudContext.restore();
      });

      const laneGradient = galaxyCloudContext.createLinearGradient(-width * 0.48, -height * 0.045, width * 0.52, height * 0.06);
      laneGradient.addColorStop(0, "rgba(0, 0, 0, 0)");
      laneGradient.addColorStop(0.34, "rgba(0, 0, 0, 0.08)");
      laneGradient.addColorStop(0.52, "rgba(0, 0, 0, 0.16)");
      laneGradient.addColorStop(0.7, "rgba(0, 0, 0, 0.07)");
      laneGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      galaxyCloudContext.globalAlpha = 0.72;
      galaxyCloudContext.fillStyle = laneGradient;
      galaxyCloudContext.fillRect(-width * 0.54, -height * 0.055, width * 1.08, height * 0.11);

      galaxyCloudContext.filter = "none";
      galaxyCloudContext.restore();
    }

    function drawGalaxyCloud() {
      if (!galaxyCloudContext) return;
      ctx.drawImage(galaxyCloudCanvas, 0, 0, width, height);
    }

    function shiftedStarColor(star: Star, radialProgress: number) {
      const { r, g, b } = star.color;
      /*
        Visual-only relativity cue:
        Forward motion makes stars near the travel direction read cooler/bluer
        and brighter, while stars that have swept toward the sides drift slightly
        warmer/redder. This is not a strict spectral simulation; it is a subtle
        art-directed approximation of Doppler blueshift/redshift and beaming so
        the background still stays calm behind the product UI.
      */
      const blueShift = Math.max(0, 1 - radialProgress) * 0.28;
      const redShift = Math.max(0, radialProgress - 0.58) * 0.2;

      return {
        r: Math.min(255, r + redShift * 70 - blueShift * 20),
        g: Math.min(255, g + blueShift * 28 + redShift * 16),
        b: Math.min(255, b + blueShift * 56 - redShift * 24)
      };
    }

    function drawStar(star: Star, x: number, y: number, alpha: number, radialProgress: number, trailLength: number, directionX: number, directionY: number) {
      const { r, g, b } = shiftedStarColor(star, radialProgress);
      const highDetailStar = star.radius >= 0.9 || star.glow > 1.45;

      if (highDetailStar && star.glow > 0.65) {
        const glowRadius = star.radius * (3.7 + star.glow);
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, glowRadius);
        gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${Math.min(0.56, alpha * 0.56)})`);
        gradient.addColorStop(0.23, `rgba(${r}, ${g}, ${b}, ${alpha * 0.13})`);
        gradient.addColorStop(0.65, `rgba(${r}, ${g}, ${b}, ${alpha * 0.03})`);
        gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
        ctx.globalAlpha = 1;
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
        ctx.fill();
      }

      if (highDetailStar && trailLength > 0.8) {
        const tailX = x - directionX * trailLength;
        const tailY = y - directionY * trailLength;
        const trail = ctx.createLinearGradient(tailX, tailY, x, y);
        trail.addColorStop(0, `rgba(${Math.min(255, r + 28)}, ${Math.max(0, g - 6)}, ${Math.max(0, b - 34)}, 0)`);
        trail.addColorStop(0.58, `rgba(${r}, ${g}, ${b}, ${alpha * 0.12})`);
        trail.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${alpha * 0.42})`);
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.lineCap = "round";
        ctx.lineWidth = Math.max(0.35, star.radius * 0.42);
        ctx.strokeStyle = trail;
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.restore();
      }

      ctx.globalAlpha = alpha;
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.beginPath();
      ctx.arc(x, y, star.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    function drawStars(time: number) {
      const focusX = width * 0.5;
      const focusY = height * 0.48;
      const maxRadius = Math.hypot(width, height) * 0.56;

      for (const star of stars) {
        const baseX = star.x - focusX;
        const baseY = star.y - focusY;
        const baseDistance = Math.max(24, Math.hypot(baseX, baseY));
        const directionX = baseX / baseDistance;
        const directionY = baseY / baseDistance;
        const travel = reducedMotion ? star.depth : (star.depth + time * star.speed) % 1;
        const radialProgress = star.edgeField
          ? 0.66 + Math.pow(travel, 1.35) * 0.42
          : 0.24 + Math.pow(travel, 1.55) * 1.5;
        const focusGather = star.edgeField ? 0 : Math.max(0, 1 - travel) * 0.18;
        const x = focusX + directionX * baseDistance * radialProgress + Math.sin(time * 0.00012 + star.phase) * focusGather * 8;
        const y = focusY + directionY * baseDistance * radialProgress + Math.cos(time * 0.0001 + star.phase) * focusGather * 6;
        const distanceFromFocus = Math.hypot(x - focusX, y - focusY);
        const edgeProgress = Math.min(1, distanceFromFocus / maxRadius);
        const twinkle = reducedMotion ? 0 : Math.sin(time * 0.00135 + star.phase) * star.twinkle;
        const beaming = star.edgeField ? 0.11 : 0.08 + Math.max(0, 1 - edgeProgress) * 0.18;
        const targetAlpha = Math.max(0.035, Math.min(1, star.alpha + twinkle + beaming));
        const fadeIn = smoothStep(0, star.edgeField ? 0.16 : 0.22, travel);
        const fadeOut = 1 - smoothStep(star.edgeField ? 0.9 : 0.86, 1, travel);
        const lifecycleFade = reducedMotion ? 1 : Math.min(fadeIn, fadeOut);
        const alpha = targetAlpha * lifecycleFade;
        const trailLength = reducedMotion ? 0 : Math.max(0, Math.pow(edgeProgress, 1.7) * 18 - 4) * lifecycleFade;

        if (x < -32 || x > width + 32 || y < -32 || y > height + 32) {
          continue;
        }

        if (alpha <= 0.004) {
          continue;
        }

        drawStar(star, x, y, alpha, edgeProgress, trailLength, directionX, directionY);
      }

      ctx.globalAlpha = 1;
    }

    function drawMeteor(delta: number) {
      if (reducedMotion) return;

      if (!meteor.active) {
        meteor.delay -= delta;
        if (meteor.delay <= 0) {
          meteor = createMeteor(width, height, random);
        }
        return;
      }

      meteor.elapsed += delta;
      meteor.progress = Math.min(1, meteor.elapsed / meteor.duration);
      const easeProgress = 1 - Math.pow(1 - meteor.progress, 2);
      const headX = meteor.x + meteor.dx * meteor.distance * easeProgress;
      const headY = meteor.y + meteor.dy * meteor.distance * easeProgress;
      const tailX = headX - meteor.dx * meteor.length;
      const tailY = headY - meteor.dy * meteor.length;
      const fade = Math.sin(meteor.progress * Math.PI);
      const trail = ctx.createLinearGradient(tailX, tailY, headX, headY);
      trail.addColorStop(0, "rgba(91, 141, 239, 0)");
      trail.addColorStop(0.5, `rgba(132, 188, 255, ${0.22 * fade})`);
      trail.addColorStop(1, `rgba(255, 255, 255, ${0.95 * fade})`);

      ctx.save();
      ctx.lineCap = "round";
      ctx.lineWidth = 1.4 + fade * 1.4;
      ctx.strokeStyle = trail;
      ctx.shadowColor = "rgba(91, 141, 239, 0.72)";
      ctx.shadowBlur = 18;
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(headX, headY);
      ctx.stroke();
      ctx.restore();

      if (meteor.progress >= 1) {
        meteor.active = false;
      }
    }

    function draw(time: number) {
      if (time - lastTime < frameInterval) {
        animationFrame = window.requestAnimationFrame(draw);
        return;
      }

      const delta = Math.min(64, time - lastTime);
      lastTime = time;

      ctx.clearRect(0, 0, width, height);
      drawGalaxyCloud();
      drawStars(time);
      drawMeteor(delta);

      animationFrame = window.requestAnimationFrame(draw);
    }

    resize();
    draw(lastTime);
    window.addEventListener("resize", resize);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas className="galaxy-background" ref={canvasRef} aria-hidden="true" />;
}
